import { NetworkStatus } from "@apollo/client";
import getUnixTime from "date-fns/getUnixTime";
import subDays from "date-fns/subDays";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import InfiniteLoader from "react-window-infinite-loader";

import { PollingIntervalGroups } from "apollo/constants";
import fetchMoreUpdateQueryPreviousFix from "apollo/fetchMoreUpdateQueryPreviousFix";
import usePolledQuery from "apollo/usePolledQuery";
import NotFoundPage from "components/error/NotFoundPage";
import {
  getFiltersPredicationFromURI,
  getSortOptionFromURI,
  makeConstraintByType,
} from "components/Filters/helpers";
import { SavedFilterView } from "components/Filters/types";
import FlashContext from "components/FlashMessages/FlashContext";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import ListContentWrapper from "components/ListContentWrapper";
import ListEntitiesNew from "components/ListEntitiesNew";
import PageLoading from "components/loading/PageLoading";
import { getSearchQuery } from "components/SearchInput/helpers";
import DatepickerRange from "ds/components/Datepicker/Range";
import { DateRange } from "ds/components/Datepicker/types";
import useDatepickerRange from "ds/components/Datepicker/useDatepickerRange";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageAuditTrail } from "hooks/useAnalytics/pages/auditTrail";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import {
  BillingTierFeature,
  SearchAuditTrailEntriesOutput,
  SearchQueryFieldConstraintTimeInLast,
  SearchSuggestionsFieldType,
} from "types/generated";
import { uniqByKey } from "utils/uniq";
import useTierFeature from "views/Account/hooks/useTierFeature";
import { AccountContext } from "views/AccountWrapper";

import AuditTrialEntriesFiltersLayout from "./components/FiltersLayout";
import AuditTrailListItemVirtualized from "./components/ListItem/Virtualized";
import AuditTrailEntriesTableHeader from "./components/TableHeader";
import {
  AUDIT_TRAIL_ENTRIES_PER_PAGE,
  INITIAL_PERIOD,
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
  PERIODS,
} from "./constants";
import { SEARCH_AUDIT_TRAIL_ENTRIES } from "./gql";

const maxDate = new Date();

const AuditTrailEntries = () => {
  const [currentSavedView, setCurrentSavedView] = useState<SavedFilterView | undefined>(undefined);
  const { accountName } = useTypedContext(AccountContext);
  const { onError } = useTypedContext(FlashContext);
  const [urlParams] = useSearchParams();
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageAuditTrail.Logs,
  });

  const { dateRange, datePeriod, handleSetDateRange } = useDatepickerRange({
    initialPeriod: INITIAL_PERIOD,
    periods: PERIODS,
  });

  const onSetDateRange = (range: DateRange, period?: SearchQueryFieldConstraintTimeInLast) => {
    handleSetDateRange(range, period);
    trackSegmentAnalyticsEvent("Timerange changed", {
      period,
      range,
    });
  };

  const sortOptionFields = useMemo(
    () => getSortOptionFromURI(urlParams, INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION),
    [urlParams]
  );

  const predicates = useMemo(() => {
    const predicatesMap = getFiltersPredicationFromURI(urlParams, true);

    if (!predicatesMap) {
      return [];
    }

    if (dateRange) {
      predicatesMap?.set("createdAt", {
        field: "createdAt",
        exclude: null,
        constraint: makeConstraintByType(
          SearchSuggestionsFieldType.Time,
          typeof dateRange === "string"
            ? [dateRange]
            : [getUnixTime(dateRange.start), getUnixTime(dateRange.end)]
        ),
      });
    }

    return Array.from(predicatesMap.values());
  }, [dateRange, urlParams]);

  const searchInput = getSearchQuery(urlParams);

  const isActive = useTierFeature(BillingTierFeature.Auditing);

  const { data, error, fetchMore, networkStatus, previousData } = usePolledQuery<{
    searchAuditTrailEntries: SearchAuditTrailEntriesOutput;
    auditTrailRetentionDays: number;
  }>(SEARCH_AUDIT_TRAIL_ENTRIES, {
    nextFetchPolicy: "cache-first",
    variables: {
      input: {
        first: AUDIT_TRAIL_ENTRIES_PER_PAGE,
        after: null,
        fullTextSearch: searchInput,
        orderBy: sortOptionFields,
        predicates,
      },
    },
    pollingGroup: PollingIntervalGroups.Lists,
    skip: !isActive,
  });

  const minDate = useMemo(
    () => subDays(maxDate, data?.auditTrailRetentionDays || 0),
    [data?.auditTrailRetentionDays]
  );

  const auditTrailEntries = useMemo(
    () =>
      (data?.searchAuditTrailEntries || previousData?.searchAuditTrailEntries)?.edges.map(
        (edge) => edge.node
      ) || [],
    [data?.searchAuditTrailEntries, previousData?.searchAuditTrailEntries]
  );

  useTitle(`Audit trail · ${accountName}`);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (!isActive) {
    return (
      <EmptyState
        icon={EmptystateSearchNoResultsColored}
        title="No results"
        caption="Audit trail is not available for this account."
      />
    );
  }

  const isLoading = networkStatus === NetworkStatus.loading;

  if (isLoading && auditTrailEntries.length === 0) {
    return <PageLoading />;
  }

  if (!data?.searchAuditTrailEntries && !previousData?.searchAuditTrailEntries) {
    return <NotFoundPage />;
  }

  const isItemLoaded = (value: number) => value < auditTrailEntries.length;
  const isPageEmpty = !isLoading && !error && auditTrailEntries.length === 0;
  const hasNoResultsWithFiltering = searchInput.length > 0 || predicates.length > 0;

  const loadMoreItems = async () => {
    try {
      if (
        data?.searchAuditTrailEntries?.pageInfo.endCursor &&
        data?.searchAuditTrailEntries?.pageInfo.hasNextPage
      ) {
        await fetchMore({
          updateQuery: (prev, { fetchMoreResult }) => {
            const previousData = fetchMoreUpdateQueryPreviousFix(
              !!prev?.searchAuditTrailEntries,
              prev,
              data
            );

            if (!fetchMoreResult || !fetchMoreResult?.searchAuditTrailEntries) return previousData;

            const result = fetchMoreResult?.searchAuditTrailEntries;

            if (!result || !result?.edges?.length) return previousData;

            const edges = uniqByKey(
              [...(previousData?.searchAuditTrailEntries?.edges || []), ...result.edges],
              "cursor"
            );

            return {
              auditTrailRetentionDays: previousData.auditTrailRetentionDays,
              searchAuditTrailEntries: {
                ...result,
                edges,
              },
            };
          },
          variables: {
            input: {
              first: AUDIT_TRAIL_ENTRIES_PER_PAGE,
              after: data.searchAuditTrailEntries.pageInfo.endCursor,
              fullTextSearch: searchInput,
              predicates,
              orderBy: sortOptionFields,
            },
          },
        });
      }
    } catch (error) {
      console.log("Error loading more audit trail entries", error);
      onError(error);
    }
  };

  return (
    <>
      <PageInfoHeader title="Logs">
        <DatepickerRange
          isButton
          withTime
          periods={PERIODS}
          initialPeriod={datePeriod}
          endDate={dateRange.end}
          startDate={dateRange.start}
          onChange={onSetDateRange}
          minDate={minDate}
          maxDate={maxDate}
        />
      </PageInfoHeader>
      <ListContentWrapper>
        <AuditTrialEntriesFiltersLayout
          currentSavedView={currentSavedView}
          setCurrentSavedView={setCurrentSavedView}
          predicates={predicates}
        >
          {auditTrailEntries.length > 0 && <AuditTrailEntriesTableHeader />}

          {isLoading && auditTrailEntries.length === 0 && <PageLoading />}

          {isPageEmpty && hasNoResultsWithFiltering && (
            <EmptyState
              title="No results"
              caption="Try different filters."
              icon={EmptystateSearchNoResultsColored}
              announce
            />
          )}

          {isPageEmpty && !hasNoResultsWithFiltering && (
            <EmptyState title="There are no audit trail entries yet." />
          )}

          {!isPageEmpty && (
            <InfiniteLoader
              isItemLoaded={isItemLoaded}
              itemCount={auditTrailEntries.length + AUDIT_TRAIL_ENTRIES_PER_PAGE}
              loadMoreItems={loadMoreItems}
            >
              {({ onItemsRendered }) => (
                <ListEntitiesNew
                  itemCount={auditTrailEntries.length}
                  itemProps={{
                    items: auditTrailEntries,
                  }}
                  virtualizedItem={AuditTrailListItemVirtualized}
                  itemKey={(index) => auditTrailEntries[index].id}
                  onItemsRendered={onItemsRendered}
                />
              )}
            </InfiniteLoader>
          )}
        </AuditTrialEntriesFiltersLayout>
      </ListContentWrapper>
    </>
  );
};

export default AuditTrailEntries;
